import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function RegistrationProgressDialog({
  open,
  onOpenChange,
  status,
  error,
  onComplete,
}) {
  const navigate = useNavigate();

  const getStatusContent = () => {
    switch (status) {
      case "creating":
        return {
          icon: <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />,
          title: "Registering Your Account",
          description: "Please wait while we set up your account...",
          showButton: false,
        };

      case "updating":
        return {
          icon: <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />,
          title: "Updating Account Information",
          description: "Saving your information to our secure database...",
          showButton: false,
        };

      case "success":
        return {
          icon: <CheckCircle className="h-12 w-12 text-green-500" />,
          title: "Account Created Successfully!",
          description:
            "Your account has been created. You can now proceed to complete KYC verification.",
          showButton: true,
          buttonText: "Go to Home",
          buttonAction: () => {
            onOpenChange(false);
            navigate("/");
          },
        };

      case "error":
        return {
          icon: <XCircle className="h-12 w-12 text-red-500" />,
          title: "Registration Failed",
          description:
            error ||
            "An error occurred while creating your account. Please try again.",
          showButton: true,
          buttonText: "Try Again",
          buttonAction: () => {
            onOpenChange(false);
            if (onComplete) onComplete();
          },
        };

      default:
        return {
          icon: null,
          title: "",
          description: "",
          showButton: false,
        };
    }
  };

  const content = getStatusContent();

  // Auto-navigate to home after success
  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => {
        onOpenChange(false);
        navigate("/");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={
        status === "creating" || status === "updating" ? () => {} : onOpenChange
      }
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">{content.title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-4 py-4">
          {content.icon}

          <DialogDescription className="text-center">
            {content.description}
          </DialogDescription>

          {false && status === "creating" && (
            <div className="w-full space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Validating information...</span>
              </div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Creating user account...</span>
              </div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Updating database...</span>
              </div>
            </div>
          )}

          {content.showButton && (
            <Button
              onClick={content.buttonAction}
              className="w-full"
              variant={status === "error" ? "destructive" : "default"}
            >
              {content.buttonText}
            </Button>
          )}

          {status === "success" && (
            <p className="text-xs text-muted-foreground text-center">
              Redirecting to home in 5 seconds...
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
